<template>
    <div id="ProjectEditor" class="text-left" :style="{ paddingRight: assistantPush ? assistantPush + 'px' : 0 }">
        <div class="project-editor-header">
            <InputText
                v-model="project.name"
                @change="onChangeName"
                class="project-name-input w-full border-0 focus:border-50"
            ></InputText>
            <PrimeButton
                v-if="project.id"
                :icon="isFavorite ? 'pi pi-star-fill' : 'pi pi-star'"
                class="p-button-text p-button-rounded favorite-project-button"
                :class="{ active: isFavorite }"
                :title="isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'"
                :aria-label="isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'"
                @click="toggleFavorite"
            />
            <PrimeButton
                v-if="project.id"
                icon="pi pi-android"
                class="p-button-text p-button-rounded assistant-toggle-button"
                :class="{ active: assistantVisible }"
                title="AI-помощник"
                aria-label="AI-помощник"
                @click="assistantVisible = !assistantVisible"
            />
        </div>
        <div class="project-editor-tabs">
            <TabView @tab-change="onTabChange" class="tab-view-custom">
                <TabPanel header="Шаблон" >
                    <ProjectTemplateManager
                        v-if="mode==='template' && project.id"
                        :key="'template-' + project.id + '-' + templateVersion"
                        :project="project"
                        :data-version="dataVersion"
                    ></ProjectTemplateManager>
                </TabPanel>
                <TabPanel header="Данные" >
                    <ProjectDataManager
                        v-if="mode==='data' && project.id"
                        :key="'data-' + project.id + '-' + dataVersion"
                        :project="project"
                        @data-source-changed="onDataSourceChanged"
                    ></ProjectDataManager>
                </TabPanel>
              <TabPanel header="Публичный доступ" >
                <ProjectPublicAccess
                    v-if="mode==='access' && project.id"
                    :key="'access-' + project.id"
                    :project="project"
                    @changeAccessOptions="onChangeAccessOptions"
                ></ProjectPublicAccess>
              </TabPanel>
            </TabView>
        </div>

        <AssistantPanel
            v-if="project.id"
            :project="project"
            :visible="assistantVisible"
            @close="assistantVisible = false"
            @resize="assistantPush = $event"
            @actions-applied="onAssistantActionsApplied"
        />
    </div>

</template>

<script>
import api from "@/libs/api";
import EventBus from "@/libs/event-bus";
import events from "@/libs/events";
import projectShortcuts from "@/libs/projectShortcuts";
import { skipNextTemplateUnmountSave } from "@/libs/templateSaveGuard";

import ProjectTemplateManager from "@/components/ProjectTemplateManager";
import ProjectDataManager from "@/components/ProjectDataManager";
import ProjectPublicAccess from "@/components/ProjectPublicAccess.vue";
import AssistantPanel from "@/components/assistant/AssistantPanel.vue";

export default {
    components:{
      ProjectTemplateManager,
      ProjectDataManager,
      ProjectPublicAccess,
      AssistantPanel
    },
    data(){
        return {
            mode: 'template',
            project_id: null,
            project: {
                name: ''
            },
            isFavorite: false,
            assistantVisible: false,
            assistantPush: 0,
            templateVersion: 0,
            dataVersion: 0,
        }
    },
    methods:{
        loadProject(projectId) {
            this.project_id = projectId;
            this.project = {
                name: ''
            };
            this.isFavorite = false;

            api.viewProject(this.project_id).then(data => {
                if (String(projectId) !== String(this.project_id)) {
                    return;
                }

                this.project = data;
                projectShortcuts.addRecentProject(data);
                this.isFavorite = projectShortcuts.isFavoriteProject(data.id);
                EventBus.$emit(events.PROJECT_SHORTCUTS_UPDATED);
            });
        },
        onTabChange(e){
            switch (e.index){
                case 0:
                    this.mode =  'template';
                    break;

                case 1:
                    this.mode =  'data';
                    break;

                case 2:
                  this.mode =  'access';
                  break;
            }
        },

        onChangeAccessOptions(options){
          this.project.public = options.allow ? 1 : 0;
          this.project.password_field = options.access_field;
          this.project.access_comment = options.access_comment;
        },
        onDataSourceChanged(source) {
            this.project.data_source = source;
        },
        onAssistantActionsApplied({ affectsTemplate, affectsData }) {
            // Смена key заставляет вкладку перезагрузить состояние с сервера,
            // чтобы канвас шаблона и таблица данных отразили действия ассистента.
            if (affectsTemplate) {
                // Уходящий экземпляр редактора держит устаревшее состояние: его autosave
                // при размонтировании затрёт изменения ассистента — подавляем его.
                if (this.mode === 'template') skipNextTemplateUnmountSave();
                this.templateVersion++;
            }
            if (affectsData) {
                // Ассистент заводит данные в ручном режиме — подтягиваем признак сразу,
                // чтобы вкладка «Данные» не показывала экран выбора источника.
                if (!this.project.data_source) this.project.data_source = 'manual';
                this.dataVersion++;
            }
        },
        onChangeName(){
            api.updateProjectName(this.project_id, this.project.name).then(() => {
                projectShortcuts.addRecentProject(this.project);
                if (this.isFavorite) {
                    projectShortcuts.addFavoriteProject(this.project);
                }
                EventBus.$emit(events.PROJECT_SHORTCUTS_UPDATED);
            });
        },
        toggleFavorite() {
            if (!this.project.id) {
                return;
            }

            if (this.isFavorite) {
                projectShortcuts.removeFavoriteProject(this.project.id);
                this.isFavorite = false;
            } else {
                projectShortcuts.addFavoriteProject(this.project);
                this.isFavorite = true;
            }

            EventBus.$emit(events.PROJECT_SHORTCUTS_UPDATED);
        }
    },
    computed:{
      description(){
          return this.project !== null ? JSON.stringify(this.project) : '';
      }
    },
    mounted() {
        this.loadProject(this.$route.params.project_id);
    },
    watch: {
        '$route.params.project_id'(projectId) {
            if (projectId && String(projectId) !== String(this.project_id)) {
                this.loadProject(projectId);
            }
        }
    }
}
</script>

<style lang="scss">
    #ProjectEditor {
        .project-editor-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }

        .project-name-input {
            min-width: 0;
            font-size: 1.35rem;
            font-weight: 600;
            line-height: 1.2;
            padding: 0.35rem 0;
        }

        .favorite-project-button {
            flex: 0 0 auto;
            color: #64748b;

            &.active {
                color: #f59e0b;
            }
        }

        .assistant-toggle-button {
            flex: 0 0 auto;
            color: #64748b;

            &.active {
                color: #3b82f6;
            }
        }
    }

    .p-tabview.tab-view-custom{
        color: red;

        .p-tabview-nav {
            margin-bottom: 0;
        }

        .p-tabview-nav li .p-tabview-nav-link {
            padding: 0.65rem 0.9rem;
        }

        .p-tabview-panels {
            padding: 0.75rem 0 0;
        }
    }

</style>
